/* Note: hasOwnProperty change: https://eslint.org/docs/rules/no-prototype-builtins */
import React, { useState, useEffect, useMemo, useTransition, useCallback, useDeferredValue } from "react";
import {
  CHANNEL_LINEUP_VIEW,
  KeyValuePair,
  PACKAGE_INDICATORS,
  SortOption
} from "@directv-digital/dds.services.channel-lineup-provider";
import {
  LINK_POSITION_BODY,
  fireGa4ProductImpression,
  fireLinkClickEvent,
  firePageLoadEvent,
  fireSelectItem,
  getAnalyticsAttrs
} from "@directv-digital/dds.services.analytics-service";
import { useLocationHref } from "src/hooks/useLocationHref";
import DtvTheme from "@directv-digital/dds.ui.dtv-theme";

const bps = DtvTheme.breakpoints;

import DtvBox from "@directv-digital/dds.ui.dtv-box";
import DtvButton, { IDtvButtonVariation, IDtvMode } from "@directv-digital/dds.ui.dtv-button";
import DtvGrid from "@directv-digital/dds.ui.dtv-grid";
import DtvText from "@directv-digital/dds.ui.dtv-text";
import RadioButton from "@directv-digital/fusion.atoms.radio-button";
import Table from "@mui/material/Table";
import TableHead from "@mui/material/TableHead";
import TableBody from "@mui/material/TableBody";
import Accordion from "@mui/material/Accordion";
import AccordionDetails from "@mui/material/AccordionDetails";
import Paper from "@mui/material/Paper";
import LazyLoad from 'react-lazyload';

// local components
import { CategoryHeader, ChannelRow, SvodChannelRow } from "./components";
import { channelColWidthStyle, ssrStyles } from "./PackageCmprGrid.styles";
import { IChannelsUI } from "../ChlupStore";
import { EmbeddedSvods, IIndicatorDataset, IPackage } from "../chnlListingPage.props";
import { getMaxPackageDim } from "../utils/utilityFunctions"; // publish this
import { zIndexMap } from "../utils/zIndexMap";
import { IChnlLineupThemeUI } from "../server/themeConfig";
import MyFreeDtvBanner, { MyFreeDtvBannerProps, IMyFreeDtvChannel } from "../components/MyFreeDtvBanner";

export interface PackageComparisonGridProps {
  channelsAZ: IChannelsUI["cmprGridChannelsAZ"];
  channelsFilteredByNumber?: IChannelsUI["cmprGridChannelsNum"];
  channelsCategorical: IChannelsUI["cmprGridChannelsCategorical"];
  myFreeDtvChannels?: IMyFreeDtvChannel[];
  categoriesAZ: KeyValuePair[];
  categoriesCategorical: KeyValuePair[];
  initViewMode?: SortOption;
  sortOptions?: SortOption[];
  searchListActive?: boolean;
  packages?: IPackage[];
  myFreeDirectvBanner?: MyFreeDtvBannerProps;
  pkgIndicatorIcons?: IIndicatorDataset;
  pkgIndicatorTooltipPrefixText?: IIndicatorDataset;
  addOnPromoLogoInfo?: IIndicatorDataset;
  theming?: IChnlLineupThemeUI["packagesTab"];
  noMatchPageMsg?: string;
  embeddedSvods?: EmbeddedSvods[];
  isEmbeddedSvodsEnabled?: boolean;
  rsnTierLegalTextObj?: any;
  myFreeConfig?: any;
}

const handlePkgsHeaderCtaClick = (pkgInd: number, pkgName: string) => {
  fireSelectItem({
    items: {
      item_id: pkgInd,
      item_name: pkgName,
      item_variant: '',
      affilation: '',
      coupon: '',
      discount: '',
      index: pkgInd,
      item_list_id: 'ChannelLineup-PackagesHeader',
      item_list_name: "Channel Lineup Packages",
      price: "",
      quantity: "1"
    }
  })
}

function fireLoadAnalytics(pkgs) {
  if (pkgs?.length > 0) {
    const cardDataGA = pkgs
      ?.filter(pkg => Object.prototype.hasOwnProperty.call(pkg, "googleAnalytics"))
      ?.map((pkg, index) => ({
        id: pkg.googleAnalytics.id,
        position: index + 1
      }));
    if (cardDataGA?.length) {
      fireGa4ProductImpression(cardDataGA);
    }
  }

  firePageLoadEvent({
    "page.category.applicationName": "UF", // APPLICATION_NAME,
    "page.category.pageFunction": "Other", // PAGE_FUNCTION,
    "page.category.pageOwnership": "Video", // OWNERSHIP,
    "page.pageInfo.flowCode": "Prospect", // FLOW_CODE,
    "page.pageInfo.lineOfBusiness": "Video", // LINE_OF_BUSINESS,
    "page.location.url": window.location.pathname
  });
}

const PackageComparisonGrid = (props: PackageComparisonGridProps) => {
  const {
    channelsAZ,
    channelsFilteredByNumber,
    channelsCategorical,
    myFreeDtvChannels,
    categoriesAZ,
    categoriesCategorical,
    searchListActive,
    sortOptions,
    packages,
    pkgIndicatorIcons,
    pkgIndicatorTooltipPrefixText,
    addOnPromoLogoInfo,
    myFreeDirectvBanner,
    theming,
    noMatchPageMsg,
    embeddedSvods,
    isEmbeddedSvodsEnabled,
    rsnTierLegalTextObj,
    myFreeConfig
  } = props;

  const chnlPackageData = useMemo(() => packages?.map(pkg => ({
    pkgName: pkg?.packageConfig?.displayLabel,
    indIx: pkg?.packageIndicatorIndex
  })), [packages]);

  /** ------------------------------------------------------------------------------- */
  /** Table State and Effects --------------------------------------------------------
  **/
  /** useDeferredValue
   *  - used to delay the re-render of the component until the user stops typing
   *  - If viewMode is being used for a HEAVY calculation that may suspend the table, useviewMode
   *      - otherwise, use viewMode directly for instant updates
   */
  const [viewMode, setViewMode] = useState(props?.initViewMode);
  const contentViewMode = useDeferredValue(viewMode);
  // Search-collapse: when a channel is actively searched (a chip is selected) while
  // in CATEGORY view, render the A-Z grouping (one "Included channels" row + one
  // "Add-on channels" row) instead of repeating the channel under every content-category
  // accordion. Mirrors the modal (ChnlComparisonGrid). searchListActive is the lineup's
  // chip signal (searchChannelList.length > 0) and the AZ map is already search-filtered.
  // Browsing (no chip) keeps the content-category accordions unchanged.
  const isCategorySearchCollapse = searchListActive && contentViewMode?.key === CHANNEL_LINEUP_VIEW.CATEGORY;
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [isPending, startTransition] = useTransition();
  const locationHref = useLocationHref();

  const accByCategoryIterable = Array.from(Array(categoriesCategorical?.length));
  const [accStateByAZ, setAccStateByAZ] = useState(
    isEmbeddedSvodsEnabled ? [{ expanded: true }, { expanded: true }, { expanded: false }, { expanded: false }] : [{ expanded: true }, { expanded: false }, { expanded: false }]
  );
  const [accStateByCategory, setAccStateByCategory] = useState(
    accByCategoryIterable?.map((_, ix) => (ix <= (isEmbeddedSvodsEnabled ? 2 : 1) ? { expanded: true } : { expanded: false }))
  );

  /**
   * Channel content which is to be displayed
   * - Uses contentViewMode since it is a HEAVY calculation
   */
  const displayChannels = useMemo(() => (contentViewMode?.key === CHANNEL_LINEUP_VIEW.CHANNELS || isCategorySearchCollapse ? channelsAZ  : contentViewMode?.key === CHANNEL_LINEUP_VIEW.CHANNELNUMBER  ? channelsFilteredByNumber : channelsCategorical), [channelsAZ, channelsCategorical, channelsFilteredByNumber, contentViewMode, isCategorySearchCollapse]);

  const displayCategories = useMemo(() => (contentViewMode?.key === CHANNEL_LINEUP_VIEW.CHANNELS || contentViewMode?.key === CHANNEL_LINEUP_VIEW.CHANNELNUMBER || isCategorySearchCollapse ? categoriesAZ : categoriesCategorical), [categoriesAZ, categoriesCategorical, contentViewMode, isCategorySearchCollapse]);

  /** ------------------------------------------------------------------------------- */
  /** Dynamic Styles ----------------------------------------------------------------
   **/
  const packagesLength = useMemo(() => packages?.length || 4, [packages]);
  const mainColWidthStyle = useMemo(() => ({ // subtract 130px for the channel column
    flexBasis: `calc((100% - 215px) / ${packagesLength})`,
    width: `calc((100% - 215px) / ${packagesLength})`,
    [bps.up(bps.values.md)]: {
      flexBasis: `calc((100% - 150px) / ${packagesLength})`,
      width: `calc((100% - 150px) / ${packagesLength})`
    },
    [bps.up(350)]: {
      flexBasis: `calc((100% - 130px) / ${packagesLength})`,
      width: `calc((100% - 130px) / ${packagesLength})`
    },
    [bps.down(350)]: {
      flexBasis: `calc((100% - 110px) / ${packagesLength})`,
      width: `calc((100% - 110px) / ${packagesLength})`
    }
  }), [packagesLength]);

  const svodRowWidth = useMemo(() => ({ // subtract 130px for the channel column
    flexBasis: `calc(100% - 215px)`,
    width: `calc(100% - 215px)`,
    [bps.up(bps.values.md)]: {
      flexBasis: `calc(100% - 150px)`,
      width: `calc(100% - 150px)`
    },
    [bps.up(350)]: {
      flexBasis: `calc(100% - 130px)`,
      width: `calc(100% - 130px)`
    },
    [bps.down(350)]: {
      flexBasis: `calc(100% - 110px)`,
      width: `calc(100% - 110px)`
    }
  }), [packagesLength]);

  /** -----------------------------------------------
   * Styles re-calculated pon viewport width change */
  let tableHeaderRef: HTMLTableSectionElement;
  const [verticalPkgNamesOn, setVerticalPkgNamesOn] = useState<boolean>(false);
  const [headerHeight, setHeaderHeight] = useState<number>(0);
  const [maxVertPkgDim, setMaxVertPkgDim] = useState<number>(0);
  const [hasScrolled, setHasScrolled] = useState<boolean>(false);

  const updateVerticalHeaders = (thElem: HTMLElement) => {
    // get the max width or height dimension of package names
    const maxPkgTextDim = getMaxPackageDim(
      thElem,
      ".package-text-container",
      ".package-name",
      ".package-chnl-count"
    );
    setMaxVertPkgDim(maxPkgTextDim);
    const containerWidth =
      thElem?.querySelectorAll(".package-container")?.[0]
        ?.getBoundingClientRect()?.width;

    // if vertical headers is on and the widest text is smaller than containerWidth, turn off
    if (verticalPkgNamesOn === true) {
      if ((maxPkgTextDim < containerWidth - 24)) {
        setVerticalPkgNamesOn(false);
      }
      return;
    }
    // if vertical headers is on and the longest text is longer than the containerWidth, keep on, otherwise turn off
    if (maxPkgTextDim > containerWidth - 24) {
      setVerticalPkgNamesOn(true);
      return;
    }
    setVerticalPkgNamesOn(false);
  }

  useEffect(() => {
    function onWindowResize() {
      const tableHeaderElem = tableHeaderRef || document.getElementById("chlup-table-head");
      if (tableHeaderElem) {
        updateVerticalHeaders(tableHeaderElem);
        setHeaderHeight(pv => (tableHeaderElem?.clientHeight > 0 ? tableHeaderElem?.clientHeight : pv));
      }
    };
    let scrollRafId;
    function onScroll() {
      if (scrollRafId) {
        cancelAnimationFrame(scrollRafId);
      }
      scrollRafId = requestAnimationFrame(() => {
        const tableHeaderElem = tableHeaderRef || document.getElementById("chlup-table-head");
        if (tableHeaderElem) {
          setHasScrolled(window?.scrollY > tableHeaderElem?.offsetTop);
        }
      });
    }

    window.addEventListener("resize", onWindowResize);
    window.addEventListener("scroll", onScroll);
    onWindowResize();
    onScroll();
    fireLoadAnalytics(packages);

    return () => {
      window.removeEventListener("resize", onWindowResize);
      window.removeEventListener("scroll", onScroll);
    }
  }, []);

  // when verticalHeaders switches :: Update header height, check for sticky header
  useEffect(() => {
    const tableHeaderElem = tableHeaderRef || document.getElementById("chlup-table-head");
    if (tableHeaderElem) {
      setHeaderHeight(pv => (tableHeaderElem?.clientHeight > 0 ? tableHeaderElem?.clientHeight : pv));
    }
  }, [verticalPkgNamesOn]);

  /** -------------------------------------------------------------------------
   * UI event handlers
   */
  // When a search is active, open all accordions
  useEffect(() => {
    if (searchListActive) {
      // CATEGORY + chip now renders the A-Z grouping (isCategorySearchCollapse), so its
      // accordions live in accStateByAZ — open those, same as the real A-Z views.
      if (contentViewMode?.key === CHANNEL_LINEUP_VIEW.CHANNELS || contentViewMode?.key === CHANNEL_LINEUP_VIEW.CHANNELNUMBER || isCategorySearchCollapse) {
        setAccStateByAZ(() => (isEmbeddedSvodsEnabled ? [{ expanded: true }, { expanded: true }, { expanded: true }, { expanded: true }] : [{ expanded: true }, { expanded: true }, { expanded: true }]));
      } else if (contentViewMode?.key === CHANNEL_LINEUP_VIEW.CATEGORY) {
        setAccStateByCategory(() => accByCategoryIterable.map((_, ix) => ((ix === 0 && isEmbeddedSvodsEnabled) ? { expanded: true } : { expanded: true })));
      }
    } else {
      if (contentViewMode?.key === CHANNEL_LINEUP_VIEW.CHANNELS || contentViewMode?.key === CHANNEL_LINEUP_VIEW.CHANNELNUMBER) {
        setAccStateByAZ(() => (isEmbeddedSvodsEnabled ? [{ expanded: true }, { expanded: true }, { expanded: false }, { expanded: false }] : [{ expanded: true }, { expanded: false }, { expanded: false }]));
      } else if (contentViewMode?.key === CHANNEL_LINEUP_VIEW.CATEGORY) {
        setAccStateByCategory(() => accByCategoryIterable.map((_, ix) => (ix <= (isEmbeddedSvodsEnabled ? 2 : 1) ? { expanded: true } : { expanded: false })));
      }
    }
  }, [searchListActive, viewMode]);

  const handleChangeSortOption = sortKey => {
    const option = sortOptions.find((s: SortOption) => s.key === Number(sortKey));
    startTransition(() => {
      setViewMode(option);
    });
    // setViewMode(option);

    fireLinkClickEvent({
      linkDestination: "",
      linkName: `Display_${option?.value}`,
      linkPosition: LINK_POSITION_BODY
    });
  };

  /**
   * @description To avoid page jump when closing a long accordion with a sticky header, scrolls window to the top of the sticky header element.
   * Uses a trick, if offsetTop is greater than 0, it is sticky.
   * @param id - of the sticky header element to scroll to
   */
  const scrollOntoStickyHeader = (id: string) => {
    const accSummaryElem = (document.querySelector(`#${id}`) as HTMLElement);
    if (accSummaryElem && accSummaryElem?.offsetTop > 0) {
      window.scrollBy({
        top: -accSummaryElem?.offsetTop,
        behavior: "smooth"
      });
    }
  };

  /** Open/Close individual category on header click */
  const handleCtgyHeaderClick = useCallback(index => {
    // During the CATEGORY search-collapse the A-Z grouping is what renders, so a header
    // click must toggle accStateByAZ (not accStateByCategory) to match the visible accordions.
    if (contentViewMode?.key === CHANNEL_LINEUP_VIEW.CHANNELS || contentViewMode?.key === CHANNEL_LINEUP_VIEW.CHANNELNUMBER || isCategorySearchCollapse) {
      setAccStateByAZ(prevAcc => ({
        ...prevAcc,
        [index]: { expanded: !prevAcc[index]?.expanded }
      }));
      scrollOntoStickyHeader(`CHLUP-az-accsummary-${index}`);
    } else if (contentViewMode?.key === CHANNEL_LINEUP_VIEW.CATEGORY) {
      setAccStateByCategory(prevAcc => ({
        ...prevAcc,
        [index]: { expanded: !prevAcc[index]?.expanded }
      }));
      scrollOntoStickyHeader(`CHLUP-ctg-accsummary-${index}`);
    }
    // Functional setState updates (prevAcc => …) — no read of accStateByAZ/Category, so
    // they're intentionally absent from deps to keep the callback identity stable.
  }, [contentViewMode, isCategorySearchCollapse]);

  const packageIndicatorIconMap = useMemo(() => ({
    [PACKAGE_INDICATORS.ADDON]: {
      src: pkgIndicatorIcons.addon,
      color: theming?.indicatorIcons?.addon,
      tooltipText: `${pkgIndicatorTooltipPrefixText.addon} in`,
      alt: (pkgName: string) => `${pkgIndicatorTooltipPrefixText.addon} in ${pkgName}`
    },
    [PACKAGE_INDICATORS.IN]: {
      src: pkgIndicatorIcons.included,
      color: theming?.indicatorIcons?.included,
      tooltipText: `${pkgIndicatorTooltipPrefixText.included} in`,
      alt: (pkgName: string) => `${pkgIndicatorTooltipPrefixText.included} in ${pkgName}`
    },
    [PACKAGE_INDICATORS.PROMO]: {
      src: pkgIndicatorIcons.promo,
      color: theming?.indicatorIcons?.promo,
      tooltipText: `${pkgIndicatorTooltipPrefixText.promo} in`,
      alt: (pkgName: string) => `${pkgIndicatorTooltipPrefixText.promo} in ${pkgName}`
    }
  }), [pkgIndicatorIcons, pkgIndicatorTooltipPrefixText, theming?.indicatorIcons]);

  // console.debug(
  //   "[PackagesGrid] rendering...", {
  //     channelColWidthStyle: channelColWidthStyle,
  //     mainColWidthStyle: mainColWidthStyle,
  //     verticalPkgNamesOn:verticalPkgNamesOn,
  //     headerHeight: headerHeight,
  //     accStateByAZ: accStateByAZ,
  //     accStateByCategory: accStateByCategory,
  //     channelsAZ: channelsAZ,
  //     channelsCategorical: channelsCategorical,
  //     hasScrolled: hasScrolled,
  // });

  return (
    // goodbye old friend
    <DtvBox id="chluNational-1" sx={ssrStyles.tableRootContainer}>
      <Table id="ChannelLineup-Table" aria-label="Channel Lineup Table">
        <TableHead id="chlup-table-head"
          ref={ref => {
 if (ref) {
 tableHeaderRef = ref;
}
}}
          sx={{
            width: "100%",
            zIndex: zIndexMap.TableHead,
            position: "sticky",
            top: '0px',
            background: theming?.headerScrollHideBg || "#fff"
        }}>
          <DtvBox
            id="ChannelLineup-PackagesHeader"
            component="tr"
            style={{
              ...ssrStyles.tablerowContainer,
              background: hasScrolled || theming?.isVariablePageBg ?
                theming?.headerScrollHideBg :
                theming?.headerBackground || theming?.headerScrollHideBg,
              ...(hasScrolled || theming?.isVariablePageBg) && myFreeDirectvBanner && {
                boxShadow: `0px 0px 10px 10px ${theming?.headerScrollHideBg}`
              },
              transition: "box-shadow 200ms ease-in-out",

              paddingBottom: "8px"
          }}>
            <DtvBox
              key="chlup-sort"
              id="chlup-sort-container"
              component="td"
              sx={{
                ...ssrStyles.grid.tdFullWidthOverride,
                ...ssrStyles.header.sortContainer,
                ...channelColWidthStyle,
                outline: theming?.sort?.border || "none",
                background: theming?.sort?.background
            }}>
              <Paper square elevation={0}
                sx={{
                  ...ssrStyles.header.sortPaper
              }}>
                <DtvText content="CHANNELS"
                  component="p" variant="body3" color="white.main"
                  sx={{
                    fontFamily: "roboto-regular",
                    fontWeight: 500,
                    fontSize: {
                      xs: "20px",
                      md: "24px"
                    },
                    color: theming?.sort?.textColor
                  }}
                />
                <DtvText content="Sort by"
                  id="chlup-sort-label"
                  component="p" variant="body3"
                  color="white.main"
                  sx={{
                    margin: "8px 0px",
                    color: theming?.sort?.textColor
                  }}
                />
                <DtvBox
                  sx={ssrStyles.header.radioButtonOverrides}
                  role="radiogroup"
                  aria-labelledby="chlup-sort-label"
                >
                  {sortOptions?.length > 0 && <RadioButton
                    variant="Radio Label"
                    mode={theming?.sort?.mode as IDtvMode}
                    analytics={{
                      tag: "Radiobutton",
                      event: 'Checked',
                      event_name: 'Radiobutton'
                    }}
                    radiobuttonoptions={sortOptions?.map(op => ({
                      label: op.value,
                      value: op.key,
                      checked: viewMode?.key === op.key,
                      disabled: false,
                      id: `chlup-sort-${op.key}`,
                      name: 'chlup-sort',
                      linkName: `sortby_${op.value}`,
                      linkPosition: 'panel_channel lineup',
                      linkDestination: locationHref
                    }))}
                    onChange={e => handleChangeSortOption(e?.target?.value) }
                  />}
                </DtvBox>
              </Paper>
            </DtvBox>
            {packages && packages?.map((packag, pkgInd) => {
              try{
              const {
                name,
                packageConfig,
                // eslint-disable-next-line @typescript-eslint/no-unused-vars
                googleAnalytics,
                pkgTextColor,
                pkgBgColor,
                ctaProps
              } = packag;

              return (
                <DtvBox key={`chlup-pkg-${name}`}
                  className="package-container" // !! for vertical headers
                  component="td"
                  justifyContent="center" alignItems="center"
                  sx={{
                    ...ssrStyles.grid.tdFullWidthOverride,
                    borderRadius: "8px 8px 0px 0px",
                    border: "none",
                    padding: "0px 4px",
                    display: "flex",
                    alignItems: "end", // flex to bottom of header
                    ...mainColWidthStyle
                }}>
                  <Paper
                    square
                    elevation={1}
                    sx={{
                      background: theming?.packageTiles?.[pkgInd]?.background || pkgBgColor,
                      outline: theming?.packageTiles?.[pkgInd]?.border || "none",
                      width: "100%",
                      display: "flex",
                      flexDirection: "column",
                      position: "relative",
                      borderRadius: "8px 8px 0px 0px",
                      padding: verticalPkgNamesOn ? "12px 1px 9px 1px" : "20px 15px",
                      [bps.between(bps.values.sm, bps.values.md)]: {
                        padding: verticalPkgNamesOn ? "16px 6px" : "20px 12px"
                      },
                      [bps.down(860)]: {
                        height: "237px"
                      },
                      [bps.up(bps.values.md)]: {
                        padding: verticalPkgNamesOn ? "16px 8px" : "20px 15px 15px 15px"
                      },
                      [bps.up(1440)]: {
                        height: "183px",
                        justifyContent: "center"
                      },
                      ...(verticalPkgNamesOn ? {
                        justifyContent: "flex-end",
                        alignItems: "center"
                      } : {
                        justifyContent: "space-around",
                        alignItems: "flex-start"
                      })
                  }}>
                    <DtvGrid
                      className="package-text-container" // !! for vertical headers
                      container
                      sx={{
                        width: 'fit-content',
                        height: 'fit-content',
                        display: "block",
                        ...ssrStyles.header.packageFontStyles,
                        ...(verticalPkgNamesOn && {
                          flexDirection: "column !important",
                          writingMode: "vertical-rl !important",
                          textOrientation: "mixed !important",
                          transform: "rotate(180deg) !important",
                          paddingTop: "8px",
                          height: maxVertPkgDim ? `${maxVertPkgDim }px !important` : "140px"
                        })
                      }}>
                      <DtvBox sx={{ ...verticalPkgNamesOn ? { height: "100%" } : { width: "100%" } }}>
                        <DtvText
                          content={packageConfig?.displayLabel || name}
                          className="package-name" // !! for vertical headers
                          color={pkgTextColor}
                          sx={{
                            color: theming?.packageTiles?.[pkgInd]?.packageNameText,
                            textAlign: "left",
                            fontWeight: "600 !important",
                            whiteSpace: "nowrap",
                            fontSize: "20px !important",
                            [bps.down(bps.values.md)]: {
                              fontSize: "18px !important"
                            },
                            "& sup": {
                              fontSize: "10px",
                              lineHeight: 0
                            },
                            ...verticalPkgNamesOn ? {
                              height: "fit-content"
                            } : {
                              width: "100%",
                              paddingTop: "6px"
                            }
                          }}
                        />
                      </DtvBox>
                      <DtvBox sx={{ ...verticalPkgNamesOn ? { height: "100%" } : { width: "100%" } }}>
                        <DtvText
                          content={packageConfig?.channelCount || packag?.numOfChannels}
                          className="package-chnl-count" // !! for vertical headers
                          sx={{
                            fontFamily: "roboto-regular",
                            whiteSpace: "nowrap",
                            fontSize: "14px !important",
                            textAlign: "left",
                            color: theming?.packageTiles?.[pkgInd]?.packageChannelsText,
                            lineHeight: "18.2px",
                            fontWeight: "500 !important",
                            ...(verticalPkgNamesOn ? { height: "fit-content" } : { width: "100%" })
                          }}
                        />
                      </DtvBox>
                    </DtvGrid>
                    {ctaProps && (
                      <DtvBox sx={ssrStyles.header.shopBtnContainer}>
                        <DtvButton
                          content={ctaProps?.label || "Shop"}
                          variant={ctaProps?.variant || "uxprimary" as IDtvButtonVariation}
                          mode={ctaProps?.mode || "dark" as IDtvMode}
                          label={`${ctaProps?.label}`}
                          ariaLabel={`Link to ${ctaProps?.label}`}
                          target="_parent"
                          targeturl={ctaProps?.targeturl}
                          onClick={() => handlePkgsHeaderCtaClick(pkgInd, ctaProps?.label)}
                          googleAnalyticsData = {getAnalyticsAttrs(
                            `${ctaProps?.label}_${name}`,
                            LINK_POSITION_BODY,
                            ctaProps?.targeturl?.includes("directv.com") ?
                              ctaProps?.targeturl :
                              `https://www.directv.com/${ctaProps?.targeturl}`
                          )}
                          {...ctaProps}
                          sx={ssrStyles.header.shopBtn}
                        />
                      </DtvBox>
                    )}
                  </Paper>
                </DtvBox>
              );
              } catch (error) {
                console.error("[PackagesGrid] ERROR in package loop: ", error);
                return null;
              }
            })}
          </DtvBox>
          {/* MyFreeDirectv Banner */}
          {myFreeDirectvBanner && (
            <DtvBox
              id="ChnlListingPage-MyFreeDtv-container"
              component="tr"
              sx={{
                ...ssrStyles.grid.myFreeDtvContainer,
                background: theming?.headerScrollHideBg
              }}>
              <DtvBox component="td" sx={ssrStyles.grid.tdFullWidthOverride}>
                <MyFreeDtvBanner
                  {...myFreeDirectvBanner}
                  channels={myFreeDtvChannels}
                  theming={theming?.myFreeDtvBanner}
                />
              </DtvBox>
            </DtvBox>
          )}
        </TableHead>

        <TableBody id="tableBody">
          {(embeddedSvods.length < 1) && ((Object.keys(channelsAZ)?.length === 0 || Object.keys(channelsFilteredByNumber)?.length === 0 || Object.keys(categoriesCategorical)?.length === 0)) ? (
            <DtvText
              content={noMatchPageMsg}
              variant="body1"
              sx={{
                fontSize: {
                  xs: "16px",
                  md: "20px"
                },
                color: theming?.gridText,
                textAlign: "center",
                margin: "48px 16px"
              }}
            />
          ) : contentViewMode?.key === CHANNEL_LINEUP_VIEW.CHANNELS || contentViewMode?.key === CHANNEL_LINEUP_VIEW.CHANNELNUMBER || isCategorySearchCollapse ? (
            displayCategories?.length > 0 &&
            displayCategories?.map((category, ctgyIndex) => {
              try {
                const { key: ctgKey, value: tableCtgyValue } = category;
                const isAddOnCtgy = ctgKey === "ADD_ON_CHANNELS";
                const isMyFreeDtvCtgy = ctgKey === "MYFREE_DTV";
                let showAccordion = false;

                  if (ctgKey === "INCLUDED_SUBSCRIPTIONS" && embeddedSvods.length > 0) {
                    showAccordion = true; // always show Included Subscriptions
                  } else if (ctgKey !== "ADD_ON_CHANNELS") {
                    showAccordion =
                    displayChannels?.[ctgKey]?.length > 0 &&
                      Array.isArray(displayChannels?.[ctgKey]);
                  } else {
                    showAccordion =
                      Object.keys(displayChannels?.ADD_ON_CHANNELS || {})?.length > 0;
                  }

                if (showAccordion) {
                  return (
                    <DtvBox key={`CHLUP-az-section-${tableCtgyValue}`} component="tr" sx={{ background: theming?.gridBackground }}>
                      <DtvBox component="td" sx={ssrStyles.grid.tdFullWidthOverride}>
                        <Accordion
                          key={`CHLUP-az-acc-${tableCtgyValue}`}
                          id={`CHLUP-az-acc-${tableCtgyValue}`}
                          TransitionProps={{
                            timeout: 400,
                            unmountOnExit: true
                          }}
                          expanded={accStateByAZ[ctgyIndex]?.expanded ?? false}
                          sx={ssrStyles.grid.muiAccordion}
                        >
                          <CategoryHeader
                            index={ctgyIndex}
                            id={`CHLUP-az-accsummary-${ctgyIndex}`}
                            ariaControls={`CHLUP-az-acc-details-${ctgyIndex}`}
                            isAddOnCtgyHead={isAddOnCtgy}
                            isMyFreeDtvCtgyHead={isMyFreeDtvCtgy}
                            label={tableCtgyValue}
                            expanded={accStateByAZ[ctgyIndex]?.expanded ?? false}
                            handleClick={handleCtgyHeaderClick}
                            headerHeight={headerHeight}
                            addOnPromoLogoInfo={addOnPromoLogoInfo}
                            theming={theming?.sectionHeaders}
                            myfreeBgColor={theming?.myFreeDtvBanner?.background}
                            myFreeConfig={myFreeConfig}
                          />
                          <AccordionDetails
                            id={`CHLUP-az-acc-details-${ctgyIndex}`}
                            sx={{
                              ...ssrStyles.grid.muiAccordionDetails,
                              background: isAddOnCtgy ? theming?.gridAddOnPromoBackground : theming?.gridBackground
                            }}
                          >
                              {displayChannels?.[ctgKey]?.map((channel, cIx: number) => {
                                if (channel?.name === undefined || channel?.chnlNum === undefined) {
                                  console.warn("[PackagesGrid] channel name/chnlNum is undefined, skipping", channel);
                                }

                                if (ctgyIndex > 0 || cIx > 15) {
                                  return (
                                    <LazyLoad
                                      key={channel?.uuid}
                                      offset={2500} debounce={25} once
                                      style={{
                                        minHeight: "108px",
                                        background: theming?.gridBackground,
                                        borderBottom: `1px solid ${theming?.gridBorder}`
                                      }}>
                                      <ChannelRow
                                        channel={channel}
                                        chnlPackageData={chnlPackageData}
                                        mainColWidthStyle={mainColWidthStyle}
                                        packageIndicatorIconMap={packageIndicatorIconMap}
                                        borderColor={theming?.gridBorder}
                                        textColor={isAddOnCtgy ? theming?.gridAddOnPromoText : theming?.gridText}
                                        rsnTierLegalTextObj={rsnTierLegalTextObj}
                                      />
                                    </LazyLoad>
                                  );
                                }
                                return (
                                  <ChannelRow key={channel?.uuid}
                                    channel={channel}
                                    chnlPackageData={chnlPackageData}
                                    mainColWidthStyle={mainColWidthStyle}
                                    packageIndicatorIconMap={packageIndicatorIconMap}
                                    borderColor={theming?.gridBorder}
                                    textColor={isAddOnCtgy ? theming?.gridAddOnPromoText : theming?.gridText}
                                    rsnTierLegalTextObj={rsnTierLegalTextObj}
                                  />
                                );
                              })}
                              {ctgKey === "INCLUDED_SUBSCRIPTIONS" &&
                                embeddedSvods?.map((svod, cIx: number) => (
                                    <SvodChannelRow
                                      key={`svod-${svod?.name}-${cIx}`}
                                      channel={svod}
                                      chnlPackageData={chnlPackageData}
                                      packageLength={packagesLength}
                                      svodRowWidth={svodRowWidth}
                                      packageIndicatorIconMap={packageIndicatorIconMap}
                                      borderColor={theming?.gridBorder}
                                      textColor={isAddOnCtgy ? theming?.gridAddOnPromoText : theming?.gridText}
                                    />
                                  ))
                              }
                          </AccordionDetails>
                        </Accordion>
                      </DtvBox>
                    </DtvBox>
                  );
                }
                return null;
              } catch (error) {
                console.error("[PackagesGrid] ERROR in AZ cat loop: ", error);
                return null;
              }
            })
          ) : contentViewMode?.key === CHANNEL_LINEUP_VIEW.CATEGORY && !isCategorySearchCollapse ? (
            categoriesCategorical?.length > 0 &&
            categoriesCategorical?.map((category, ctgyIndex) => {
              try {
              const { key: ctgKey, value: tableCtgyValue } = category;
              const isAddOnCtgy = ctgKey === "ADD_ON_CHANNELS";
              const isMyFreeDtvCtgy = ctgKey === "MYFREE_DTV";

              let showAccordion = false;
              if (ctgKey === "INCLUDED_SUBSCRIPTIONS" && embeddedSvods.length > 0) {
                showAccordion = true; // always show Included Subscriptions
              } else if (ctgKey !== "ADD_ON_CHANNELS") {
                showAccordion =
                  channelsCategorical?.[ctgKey]?.length > 0 &&
                  Array.isArray(channelsCategorical?.[ctgKey]);
              } else {
                showAccordion =
                  Object.keys(channelsCategorical?.ADD_ON_CHANNELS || {})?.length > 0;
              }

              if (showAccordion) {
                return (
                  <DtvBox key={`CHLUP-ctg-section-${tableCtgyValue}`} component="tr">
                    <DtvBox component="td" sx={ssrStyles.grid.tdFullWidthOverride}>
                      <Accordion
                        key={`CHLUP-ctg-acc-${tableCtgyValue}`}
                        id={`CHLUP-ctg-acc-${tableCtgyValue}`}
                        TransitionProps={{
 timeout: 400,
unmountOnExit: true
}}
                        expanded={accStateByCategory[ctgyIndex]?.expanded ?? false}
                        sx={ssrStyles.grid.muiAccordion}
                      >
                        <CategoryHeader
                          index={ctgyIndex}
                          id={`CHLUP-ctg-accsummary-${ctgyIndex}`}
                          ariaControls={`CHLUP-ctg-acc-details-${ctgyIndex}`}
                          isAddOnCtgyHead={isAddOnCtgy}
                          isMyFreeDtvCtgyHead={isMyFreeDtvCtgy}
                          label={tableCtgyValue}
                          expanded={accStateByCategory[ctgyIndex]?.expanded ?? false}
                          handleClick={handleCtgyHeaderClick}
                          headerHeight={headerHeight}
                          addOnPromoLogoInfo={addOnPromoLogoInfo}
                          theming={theming?.sectionHeaders}
                          myfreeBgColor={theming?.myFreeDtvBanner?.background}
                          myFreeConfig={myFreeConfig}
                        />
                        <AccordionDetails
                          id={`CHLUP-ctg-acc-details-${ctgyIndex}`}
                          sx={{
                            ...ssrStyles.grid.muiAccordionDetails,
                            background: isAddOnCtgy ? theming?.gridAddOnPromoBackground : theming?.gridBackground
                          }}
                        >
                            {ctgKey === "ADD_ON_CHANNELS" ? (
                              (channelsCategorical?.ADD_ON_CHANNELS && Object.keys(channelsCategorical.ADD_ON_CHANNELS).length > 0) && Object.keys(channelsCategorical?.ADD_ON_CHANNELS)?.map(subCatKey => {
                                if (channelsCategorical?.ADD_ON_CHANNELS?.[subCatKey]?.length > 0) {
                                  const chnls = channelsCategorical?.ADD_ON_CHANNELS?.[subCatKey];
                                  const displayName = categoriesCategorical?.find(cat => cat.key === subCatKey)?.value;

                                  return (
                                    <React.Fragment key={`addon-head-${displayName}`}>
                                      <DtvBox
                                        id={`CHLUP-addon-head-${displayName}`}
                                        style={{
                                          background: theming?.sectionHeaders?.background,
                                          border: theming?.sectionHeaders?.border || "none",
                                          width: "100%",
                                          margin: "0 !important",
                                          padding: "8px 0px 8px 18px"
                                      }}>
                                        <DtvText
                                          content={displayName}
                                          variant="smalltext"
                                          sx={{
 fontSize: "14px !important",
color: theming?.sectionHeaders?.textColor
}}
                                        />
                                      </DtvBox>
                                      {chnls?.map(channel => {
                                          if (channel?.name === undefined || channel?.chnlNum === undefined) {
                                            console.warn("[PackagesGrid] channel name/chnlNum is undefined, skipping", channel);
                                          }
                                          return (
                                            <LazyLoad
                                              key={channel?.uuid}
                                              offset={2500} debounce={25} once
                                              style={{
                                                minHeight: "108px",
                                                background: theming?.gridBackground,
                                                borderBottom: `1px solid ${theming?.gridBorder}`
                                              }}>
                                              <ChannelRow
                                                channel={channel}
                                                chnlPackageData={chnlPackageData}
                                                mainColWidthStyle={mainColWidthStyle}
                                                packageIndicatorIconMap={packageIndicatorIconMap}
                                                borderColor={theming?.gridBorder}
                                                textColor={theming?.gridAddOnPromoText}
                                                rsnTierLegalTextObj={rsnTierLegalTextObj}
                                              />
                                            </LazyLoad>
                                          );
                                      })}
                                    </React.Fragment>
                                  );
                                }
                                return null;
                              })
                            ) : ctgKey === "INCLUDED_SUBSCRIPTIONS" ? (
                              embeddedSvods?.map((svod, cIx: number) => (
                                  <SvodChannelRow
                                    key={`svod-${svod?.name}-${cIx}`}
                                    channel={svod}
                                    chnlPackageData={chnlPackageData}
                                    packageLength={packagesLength}
                                    svodRowWidth={svodRowWidth}
                                    packageIndicatorIconMap={packageIndicatorIconMap}
                                    borderColor={theming?.gridBorder}
                                    textColor={theming?.gridAddOnPromoText}
                                  />
                                ))
                            ) : channelsCategorical?.[ctgKey]?.map(channel => {
                              if (channel?.name === undefined || channel?.chnlNum === undefined) {
                                console.warn("[PackagesGrid] channel name/chnlNum is undefined, skipping", channel);
                              }
                              if (ctgyIndex > 0) {
                                return (
                                  <LazyLoad
                                    key={channel?.uuid}
                                    offset={2500} debounce={25} once
                                    style={{
                                      minHeight: "108px",
                                      backgroundColor: isAddOnCtgy ? theming?.gridAddOnPromoBackground : theming?.gridBackground,
                                      borderBottom: `1px solid ${theming?.gridBorder}`
                                    }}>
                                    <ChannelRow
                                      channel={channel}
                                      chnlPackageData={chnlPackageData}
                                      mainColWidthStyle={mainColWidthStyle}
                                      packageIndicatorIconMap={packageIndicatorIconMap}
                                      borderColor={theming?.gridBorder}
                                      textColor={theming?.gridAddOnPromoText}
                                      rsnTierLegalTextObj={rsnTierLegalTextObj}
                                    />
                                  </LazyLoad>
                                );
                              }
                              return (
                                <ChannelRow key={channel?.uuid}
                                  channel={channel}
                                  chnlPackageData={chnlPackageData}
                                  mainColWidthStyle={mainColWidthStyle}
                                  packageIndicatorIconMap={packageIndicatorIconMap}
                                  borderColor={theming?.gridBorder}
                                  textColor={theming?.gridAddOnPromoText}
                                  rsnTierLegalTextObj={rsnTierLegalTextObj}
                                />
                              );
                            })}
                        </AccordionDetails>
                      </Accordion>
                    </DtvBox>
                  </DtvBox>
                );
              }
              return null;
              } catch (error) {
                console.error("[PackagesGrid] ERROR in Categorical cat loop: ", error);
                return null;
              }
            })
          ) : null}
        </TableBody>
      </Table>
    </DtvBox>
  );
}

export const MemoizedPackageComparisonGrid = React.memo(PackageComparisonGrid);
